import React, { useCallback, useEffect, useMemo, useState } from 'react';
import './Workspace.css';
import { useAuth } from '../../contexts/AuthContext';
import Dashboard from './Dashboard';
import MarketSection from './MarketSection';
import EmployeesSection from './EmployeesSection';
import AiAssistantSection from './AiAssistantSection';
import AdminPanel from './admin/AdminPanel';
import { AdminNavDropdown } from './admin/adminUi';
import type { AdminNav } from './admin/adminShared';
import AiAssistantChat, {
  AI_ASSISTANT_INITIAL_MESSAGES,
  AiAssistantMeta,
} from './AiAssistantChat';
import { ChatMessage } from '../../services/aiAssistantService';
import {
  AiAssistantAccessInfo,
  fetchAiAssistantAccess,
} from '../../services/aiAssistantAccessService';
import { fetchAdminAccess, buildAdminPermissionsState, readAnalystPreviewEnabled, writeAnalystPreviewEnabled, type AdminAccessInfo } from '../../services/adminAccessService';
import {
  isDemoPresentationUser,
  readWorkspaceDemoRole,
  resolveWorkspaceAdminAccess,
  writeWorkspaceDemoRole,
  type DemoRoleMode,
} from '../../services/demoRoleService';
import { AdminPermissionsProvider } from './admin/adminPermissions';
import { DemoRoleSwitch } from './DemoRoleSwitch';

const ADMIN_PANEL_ENABLED = true;

function getInitials(name: string | null | undefined, email: string | null | undefined) {
  if (name && name.trim().length > 0) {
    const parts = name.trim().split(' ');
    if (parts.length === 1) return parts[0][0].toUpperCase();
    return (parts[0][0] + (parts[1][0] || '')).toUpperCase();
  }
  if (email && email.length > 0) return email[0].toUpperCase();
  return '?';
}

const DEFAULT_AI_ACCESS: AiAssistantAccessInfo = {
  hasAccess: false,
  mode: 'none',
  trialLimit: 3,
  trialUsed: 0,
  trialRemaining: 0,
};

const Workspace: React.FC = () => {
  const AI_CHAT_STORAGE_KEY = 'workspace_ai_chat_state_v1';
  const { user, signOut } = useAuth();
  type SectionType = 'dashboard' | 'data' | 'employees' | 'ai-assistant' | 'admin';
  const validSections: SectionType[] = ADMIN_PANEL_ENABLED
    ? ['dashboard', 'data', 'employees', 'ai-assistant', 'admin']
    : ['dashboard', 'data', 'employees', 'ai-assistant'];
  const savedSectionRaw = localStorage.getItem('workspace_active_section');
  const savedSection = (savedSectionRaw === 'market' ? 'data' : savedSectionRaw) as SectionType | null;
  const [activeSection, setActiveSection] = useState<SectionType>(() => {
    if (savedSection && validSections.includes(savedSection)) return savedSection;
    return 'dashboard';
  });
  const [marketPackageName, setMarketPackageName] = useState<string | undefined>(
    localStorage.getItem('workspace_package_name') || undefined
  );
  const [marketPackageDisplayName, setMarketPackageDisplayName] = useState<string | undefined>(
    localStorage.getItem('workspace_package_display_name') || undefined
  );
  const [isFloatingAiOpen, setIsFloatingAiOpen] = useState(false);
  const [aiSharedMessages, setAiSharedMessages] = useState<ChatMessage[]>(() => {
    const raw = localStorage.getItem(AI_CHAT_STORAGE_KEY);
    if (!raw) return AI_ASSISTANT_INITIAL_MESSAGES;
    try {
      const parsed = JSON.parse(raw) as { messages?: ChatMessage[] };
      return Array.isArray(parsed.messages) && parsed.messages.length > 0
        ? parsed.messages
        : AI_ASSISTANT_INITIAL_MESSAGES;
    } catch {
      return AI_ASSISTANT_INITIAL_MESSAGES;
    }
  });
  const [aiAssistantAccess, setAiAssistantAccess] = useState<AiAssistantAccessInfo>(DEFAULT_AI_ACCESS);
  const [aiAssistantAccessLoading, setAiAssistantAccessLoading] = useState(true);
  const [adminAccess, setAdminAccess] = useState<AdminAccessInfo | null>(null);
  const [adminAccessLoading, setAdminAccessLoading] = useState(true);
  const [analystPreview, setAnalystPreview] = useState(readAnalystPreviewEnabled);
  const [demoRole, setDemoRole] = useState<DemoRoleMode>(() => readWorkspaceDemoRole());
  const [adminNavTarget, setAdminNavTarget] = useState<AdminNav>('overview');
  const [aiSharedMeta, setAiSharedMeta] = useState<AiAssistantMeta>(() => {
    const raw = localStorage.getItem(AI_CHAT_STORAGE_KEY);
    if (!raw) return null;
    try {
      const parsed = JSON.parse(raw) as { meta?: AiAssistantMeta };
      return parsed.meta ?? null;
    } catch {
      return null;
    }
  });
  const aiHasConversation = aiSharedMessages.some((message) => message.role === 'user');
  const showFloatingAi = aiAssistantAccess.hasAccess || aiHasConversation;
  const name = user?.user_metadata?.name || user?.user_metadata?.full_name || user?.user_metadata?.display_name || '';
  const email = user?.email || '';
  const isDemoUser = isDemoPresentationUser(email);
  const initials = getInitials(name, email);

  const handleSectionChange = (section: typeof activeSection) => {
    setMarketPackageName(undefined);
    setMarketPackageDisplayName(undefined);
    localStorage.removeItem('workspace_package_name');
    localStorage.removeItem('workspace_package_display_name');
    setActiveSection(section);
    localStorage.setItem('workspace_active_section', section);
  };

  const setPreviewAsAnalyst = useCallback((enabled: boolean) => {
    setAnalystPreview(enabled);
    writeAnalystPreviewEnabled(enabled);
  }, []);

  const setDemoRoleMode = useCallback((mode: DemoRoleMode) => {
    setDemoRole(mode);
    writeWorkspaceDemoRole(mode);
  }, []);

  const effectiveAdminAccess = useMemo(
    () => resolveWorkspaceAdminAccess(adminAccess, email, demoRole),
    [adminAccess, email, demoRole],
  );

  const adminPermissions = useMemo(() => {
    if (!effectiveAdminAccess) return null;
    const state = buildAdminPermissionsState(effectiveAdminAccess, analystPreview);
    return { ...state, setPreviewAsAnalyst };
  }, [effectiveAdminAccess, analystPreview, setPreviewAsAnalyst]);

  useEffect(() => {
    if (!isDemoUser) return;
    setDemoRole(readWorkspaceDemoRole());
  }, [isDemoUser, email]);

  useEffect(() => {
    if (demoRole !== 'user' || activeSection !== 'admin') return;
    setActiveSection('dashboard');
    localStorage.setItem('workspace_active_section', 'dashboard');
  }, [demoRole, activeSection]);

  useEffect(() => {
    localStorage.setItem(
      AI_CHAT_STORAGE_KEY,
      JSON.stringify({
        messages: aiSharedMessages,
        meta: aiSharedMeta,
      }),
    );
  }, [aiSharedMessages, aiSharedMeta]);

  useEffect(() => {
    if (!user) {
      setAiAssistantAccess(DEFAULT_AI_ACCESS);
      setAiAssistantAccessLoading(false);
      return;
    }

    let cancelled = false;
    setAiAssistantAccessLoading(true);
    fetchAiAssistantAccess()
      .then((access) => {
        if (!cancelled) setAiAssistantAccess(access);
      })
      .finally(() => {
        if (!cancelled) setAiAssistantAccessLoading(false);
      });

    if (ADMIN_PANEL_ENABLED) {
      setAdminAccessLoading(true);
      fetchAdminAccess()
        .then((access) => {
          if (!cancelled) setAdminAccess(access?.hasAdminPanelAccess ? access : null);
        })
        .finally(() => {
          if (!cancelled) setAdminAccessLoading(false);
        });
    }

    return () => {
      cancelled = true;
    };
  }, [user]);

  return (
    <div className="workspace">
      {/* Шапка */}
      <header className="workspace-header">
        <div className="logo-container">
          <div className="logo-placeholder">
            <div className="logo-chart">
              <div className="logo-chart-bar"></div>
            </div>
          </div>
          <a href="/" className="brand-name">datacircle</a>
        </div>
        {isDemoUser && (
          <DemoRoleSwitch
            mode={demoRole}
            onChange={setDemoRoleMode}
            compact
          />
        )}
      </header>

      <div className="workspace-content">
        {/* Левая панель инструментов */}
        <aside className="workspace-sidebar">
          <nav className="sidebar-nav">
            <div className="nav-section">
              <ul className="nav-list">
                <li className={`nav-item ${activeSection === 'dashboard' ? 'active' : ''}`}>
                  <button className="nav-link" onClick={() => handleSectionChange('dashboard')}>
                    <i className="fas fa-home"></i>
                    <span>Главная</span>
                  </button>
                </li>
              </ul>
            </div>

            <div className="nav-section">
              <h3 className="nav-title">Аналитика</h3>
              <ul className="nav-list">
                <li className={`nav-item ${activeSection === 'data' ? 'active' : ''}`}>
                  <button className="nav-link" onClick={() => handleSectionChange('data')}>
                    <i className="fas fa-database"></i>
                    <span>Обзор рынка</span>
                  </button>
                </li>
              </ul>
            </div>

            <div className="nav-section">
              <h3 className="nav-title">Инструменты</h3>
              <ul className="nav-list">
                <li className={`nav-item ${activeSection === 'employees' ? 'active' : ''}`}>
                  <button className="nav-link" onClick={() => handleSectionChange('employees')}>
                    <i className="fas fa-users"></i>
                    <span>Мои сотрудники</span>
                  </button>
                </li>
                <li className={`nav-item ${activeSection === 'ai-assistant' ? 'active' : ''}`}>
                  <button className="nav-link" onClick={() => handleSectionChange('ai-assistant')}>
                    <i className="fas fa-robot"></i>
                    <span>AI-ассистент</span>
                  </button>
                </li>
                {ADMIN_PANEL_ENABLED && adminPermissions && (
                  <AdminNavDropdown
                    activeNav={adminNavTarget}
                    isSectionActive={activeSection === 'admin'}
                    navPermissions={{
                      canManageStructure: adminPermissions.canManageStructure,
                      canEditRecords: adminPermissions.canEditRecords,
                      canGradeRecords: adminPermissions.canGradeRecords,
                    }}
                    onNav={(nav) => {
                      setAdminNavTarget(nav);
                      handleSectionChange('admin');
                    }}
                  />
                )}
              </ul>
            </div>
          </nav>
          {isDemoUser && (
            <div className="sidebar-demo-role">
              <DemoRoleSwitch mode={demoRole} onChange={setDemoRoleMode} />
            </div>
          )}

          {/* Профиль пользователя внизу */}
          <div className="sidebar-profile">
            <div className="profile-avatar">{initials}</div>
            <span className="profile-name">{name || email}</span>
            <button className="profile-logout" title="Выйти" onClick={signOut}>
              <i className="fas fa-sign-out-alt"></i>
            </button>
          </div>
        </aside>

        {/* Основная область */}
        <main className={`workspace-main${activeSection === 'admin' ? ' workspace-main--admin' : ''}`}>
          <div
            className={
              activeSection === 'data'
                ? 'main-content main-content--market'
                : activeSection === 'admin'
                  ? 'main-content main-content--admin'
                  : activeSection === 'employees'
                    ? 'main-content main-content--employees'
                    : 'main-content'
            }
          >
            {activeSection === 'dashboard' ? (
              <Dashboard
                onNavigate={handleSectionChange}
                aiAccessInfo={aiAssistantAccess}
                aiAssistantAccessLoading={aiAssistantAccessLoading}
                aiHasConversation={aiHasConversation}
              />
            ) : activeSection === 'data' ? (
              <MarketSection
                key={`market-${marketPackageName ?? 'catalog'}`}
                initialPackageName={marketPackageName}
                initialPackageDisplayName={marketPackageDisplayName}
                onBack={() => {
                  setMarketPackageName(undefined);
                  setMarketPackageDisplayName(undefined);
                  localStorage.removeItem('workspace_package_name');
                  localStorage.removeItem('workspace_package_display_name');
                }}
              />
            ) : activeSection === 'employees' ? (
              <EmployeesSection />
            ) : activeSection === 'ai-assistant' ? (
              <AiAssistantSection
                accessInfo={aiAssistantAccess}
                accessLoading={aiAssistantAccessLoading}
                onAccessInfoChange={setAiAssistantAccess}
                sharedMessages={aiSharedMessages}
                onSharedMessagesChange={setAiSharedMessages}
                sharedMeta={aiSharedMeta}
                onSharedMetaChange={setAiSharedMeta}
              />
            ) : activeSection === 'admin' ? (
              adminAccessLoading ? (
                <div style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>Проверка доступа…</div>
              ) : adminPermissions ? (
                <AdminPermissionsProvider value={adminPermissions}>
                  <AdminPanel navTarget={adminNavTarget} onNavTargetChange={setAdminNavTarget} />
                </AdminPermissionsProvider>
              ) : (
                <div className="admin-denied">
                  <h2>Нет доступа</h2>
                  <p>Админ-панель доступна пользователям с ролью admin или analyst.</p>
                </div>
              )
            ) : (
              <Dashboard onNavigate={handleSectionChange} />
            )}
          </div>
        </main>
      </div>

      {/* Floating AI button + chat (при подписке или если есть история диалога) */}
      {showFloatingAi && (
      <div className={`floating-ai-btn-wrap${isFloatingAiOpen ? ' floating-ai-btn-wrap--open' : ''}`}>
        <button
          type="button"
          className={`floating-ai-btn${isFloatingAiOpen ? ' floating-ai-btn--open' : ''}`}
          onClick={() => setIsFloatingAiOpen((prev) => !prev)}
          title="AI-ассистент"
          aria-label="Открыть AI-ассистент"
        >
          <i className={`fas ${isFloatingAiOpen ? 'fa-times' : 'fa-robot'}`} aria-hidden="true" />
        </button>
      </div>
      )}

      {showFloatingAi && isFloatingAiOpen && (
        <div
          style={{
            position: 'fixed',
            right: '22px',
            bottom: '110px',
            width: 'min(460px, calc(100vw - 32px))',
            maxHeight: '78vh',
            background: '#ffffff',
            borderRadius: '14px',
            border: '1px solid #dfe3f1',
            boxShadow: '0 20px 48px rgba(15, 23, 42, 0.22)',
            zIndex: 1099,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              padding: '12px 14px',
              borderBottom: '1px solid #edf0f5',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              background: '#f8f9ff',
            }}
          >
            <div style={{ fontSize: '14px', fontWeight: 600, color: '#1a237e' }}>
              AI-ассистент
            </div>
            <button
              type="button"
              onClick={() => setIsFloatingAiOpen(false)}
              style={{
                border: 'none',
                background: 'transparent',
                color: '#667085',
                cursor: 'pointer',
                fontSize: '16px',
                width: '26px',
                height: '26px',
                borderRadius: '6px',
              }}
              aria-label="Закрыть AI-чат"
            >
              ×
            </button>
          </div>
          <div style={{ padding: '12px', overflow: 'auto' }}>
            <AiAssistantChat
              mode="widget"
              accessInfo={aiAssistantAccess}
              onAccessInfoChange={setAiAssistantAccess}
              accessLoading={aiAssistantAccessLoading}
              sharedMessages={aiSharedMessages}
              onSharedMessagesChange={setAiSharedMessages}
              sharedMeta={aiSharedMeta}
              onSharedMetaChange={setAiSharedMeta}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default Workspace; 